import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import "./ComingSoon.css";

/**
 * ComingSoon — A professional countdown landing page.
 *
 * Props:
 *  - title        {string}  The feature name (e.g. "Research Freelancing")
 *  - subtitle     {string}  Short teaser description
 *  - features     {array}   Array of { icon: node, text: string } preview cards
 *  - launchDate   {string}  ISO date string for the countdown target
 */
const ComingSoon = ({
  title = "Something Amazing",
  subtitle = "We're putting the finishing touches on this feature. Stay tuned!",
  features = [],
  launchDate,
}) => {
  // Default: 1 week from Feb 14 2026 → Feb 21 2026 00:00:00 UTC
  const targetDate = useMemo(
    () => (launchDate ? new Date(launchDate) : new Date("2026-02-21T00:00:00")),
    [launchDate]
  );

  const calcTimeLeft = () => {
    const diff = targetDate - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(calcTimeLeft);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDate]);

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="coming-soon">
      {/* Badge */}
      <span className="coming-soon__badge">
        <span className="coming-soon__badge-dot" />
        Launching Soon
      </span>

      {/* Title */}
      <h1 className="coming-soon__title">
        <span className="coming-soon__title-accent">{title}</span> is Coming
      </h1>

      {/* Subtitle */}
      <p className="coming-soon__subtitle">{subtitle}</p>

      {/* Countdown */}
      <div className="coming-soon__countdown">
        <div className="coming-soon__time-block">
          <div className="coming-soon__time-value">{pad(timeLeft.days)}</div>
          <span className="coming-soon__time-label">Days</span>
        </div>
        <span className="coming-soon__separator">:</span>
        <div className="coming-soon__time-block">
          <div className="coming-soon__time-value">{pad(timeLeft.hours)}</div>
          <span className="coming-soon__time-label">Hours</span>
        </div>
        <span className="coming-soon__separator">:</span>
        <div className="coming-soon__time-block">
          <div className="coming-soon__time-value">{pad(timeLeft.minutes)}</div>
          <span className="coming-soon__time-label">Minutes</span>
        </div>
        <span className="coming-soon__separator">:</span>
        <div className="coming-soon__time-block">
          <div className="coming-soon__time-value">{pad(timeLeft.seconds)}</div>
          <span className="coming-soon__time-label">Seconds</span>
        </div>
      </div>

      {/* Feature preview cards */}
      {features.length > 0 && (
        <div className="coming-soon__features">
          {features.map((feat, idx) => (
            <div className="coming-soon__feature-card" key={idx}>
              <span className="coming-soon__feature-icon">{feat.icon}</span>
              <span className="coming-soon__feature-text">{feat.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Back link */}
      <Link to="/feed" className="coming-soon__back">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
        Back to Feed
      </Link>
    </div>
  );
};

export default ComingSoon;
